// Domains: the platform subdomain and the White-label custom domain.
//
//   Subdomain       PUT …/subdomain (normalized, reserved names refused,
//                   database first, Cloudflare second, #338). Changing it
//                   moves the live URL, so it asks first and says what breaks.
//   Custom domain   PUT …/custom-domain (normalized, #337; needs White-label).
//                   Cloudflare status from GET …/custom-domain/status, polled
//                   while pending, "Provision" when it isn't registered, and
//                   copyable DNS records for the customer. The CNAME target
//                   is the server's cname_target (app.scorelm.com).

import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage, isApiError } from '@/api/client'
import type { CustomDomainStatus, ProvisioningResult, TenantDetail } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  CopyButton,
  ExternalLinkIcon,
  FormField,
  InlineAlert,
  KeyValueList,
  RefreshIcon,
  SkeletonRows,
  TextInput,
  ToggleRow,
  buttonClasses,
} from '@/components/ui'
import { formatDate, hostUrl } from '@/lib/format'
import { useDebouncedValue } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { useTenant } from '../tenantContext'

const DEFAULT_CNAME_TARGET = 'app.scorelm.com'
const POLL_MS = 10_000

const STATE_BADGE: Record<TenantDetail['domains']['custom_domain_state'], ReactNode> = {
  none: null,
  pending: <Badge tone="warn">Waiting on DNS</Badge>,
  active: <Badge tone="good">Verified</Badge>,
  error: <Badge tone="bad">Error</Badge>,
  unregistered: <Badge tone="outline">Not registered</Badge>,
}

/** Lowercase, trim, strip the scheme, path and trailing dot (what the server stores). */
function normalizeDomainInput(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.$/, '')
}

const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/

/** yourleague.com is an apex; tickets.yourleague.com isn't. (Two-label heuristic, as before.) */
function isApex(host: string): boolean {
  return host.split('.').filter(Boolean).length <= 2
}

export default function DomainsTab() {
  const { tenant } = useTenant()
  return (
    <div className="grid items-start gap-4 xl:grid-cols-[1fr_1.25fr]">
      <SubdomainCard tenant={tenant} />
      <CustomDomainCard tenant={tenant} />
    </div>
  )
}

// ─── Provisioning outcome ───────────────────────────────────────────────────

function ProvisioningNote({ result }: { result: ProvisioningResult }) {
  const outcome = result.custom_domain && result.custom_domain !== 'skipped' ? result.custom_domain : result.subdomain_dns
  if (outcome === 'ok') {
    return (
      <InlineAlert tone="good" title="Saved, and Cloudflare is updated">
        {result.message ?? 'DNS and Pages now point at the new name.'}
      </InlineAlert>
    )
  }
  if (outcome === 'failed') {
    return (
      <InlineAlert tone="bad" title="Saved, but Cloudflare wasn't updated">
        {result.message ?? 'The database has the new name; DNS or Pages failed. Retry from here or fix it in Cloudflare.'}
      </InlineAlert>
    )
  }
  return (
    <InlineAlert tone="info" title="Saved; Cloudflare skipped">
      {result.message ?? 'Cloudflare isn’t configured on this backend, so DNS wasn’t touched.'}
    </InlineAlert>
  )
}

// ─── Subdomain ──────────────────────────────────────────────────────────────

function SubdomainCard({ tenant }: { tenant: TenantDetail }) {
  const { apply } = useTenant()
  const d = tenant.domains
  const host = d.subdomain_host
  const suffix = host.slice(d.subdomain.length) || '.scorelm.com'
  const [draft, setDraft] = useState(d.subdomain)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState<ProvisioningResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setDraft(d.subdomain), [d.subdomain])

  const next = draft.trim().toLowerCase()
  const changed = next !== '' && next !== d.subdomain
  const debounced = useDebouncedValue(next, 400)
  const check = useQuery(changed && debounced === next ? ['subdomain-check', next] : null, (signal) => api.tenants.checkSubdomain(next, signal))
  const checkResult = check.data && check.data.subdomain === next ? check.data : null
  const blocked = !!checkResult && !checkResult.available
  const nextHost = `${next}${suffix}`
  const customActive = d.custom_domain_state === 'active' && d.custom_domain

  return (
    <Card>
      <CardHeader title="Subdomain" description="Every league has one, even with a custom domain." />
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 break-all font-mono text-[13px] text-ink">{host}</span>
        <CopyButton value={hostUrl(host)} label="Copy URL" ariaLabel={`Copy ${hostUrl(host)}`} />
        <a href={d.subdomain_url || hostUrl(host)} target="_blank" rel="noreferrer" className={buttonClasses({ size: 'sm' })}>
          <ExternalLinkIcon size={14} /> Visit
        </a>
      </div>

      <form
        className="mt-5 grid gap-3 border-t border-line pt-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (changed && !blocked) setConfirmOpen(true)
        }}
      >
        <FormField
          label="New subdomain"
          error={error}
          hint={
            !changed
              ? `Lowercase letters, numbers and hyphens. Becomes <name>${suffix}.`
              : checkResult?.available
                ? null
                : `Will become ${nextHost}`
          }
          aside={
            changed ? (
              check.status === 'error' ? (
                <span className="text-xs text-muted">Couldn’t check</span>
              ) : checkResult ? (
                checkResult.available ? (
                  <span className="text-xs text-good">Available</span>
                ) : (
                  <span className="text-xs text-bad">{checkResult.reason === 'taken' ? 'Taken' : checkResult.reason === 'reserved' ? 'Reserved' : 'Not valid'}</span>
                )
              ) : (
                <span className="text-xs text-muted">Checking…</span>
              )
            ) : null
          }
        >
          {({ id, describedBy, invalid }) => (
            <div className="flex min-w-0 items-center gap-2">
              <TextInput
                id={id}
                aria-describedby={describedBy}
                invalid={invalid || blocked}
                mono
                autoCapitalize="off"
                autoComplete="off"
                spellCheck={false}
                value={draft}
                onChange={(e) => {
                  setError(null)
                  setResult(null)
                  setDraft(e.target.value)
                }}
                className="min-w-0 flex-1"
              />
              <span className="hidden shrink-0 font-mono text-xs text-muted sm:inline">{suffix}</span>
            </div>
          )}
        </FormField>
        {checkResult && !checkResult.available && checkResult.message ? <p className="-mt-1 text-xs text-bad">{checkResult.message}</p> : null}
        {checkResult?.available ? <p className="-mt-1 text-xs text-good">{nextHost} is available.</p> : null}
        <div>
          <Button type="submit" disabled={!changed || blocked}>
            Change subdomain…
          </Button>
        </div>
        {result ? <ProvisioningNote result={result} /> : null}
      </form>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Change the live URL?"
        confirmLabel={`Move to ${nextHost}`}
        description={
          <div className="grid gap-2">
            <p>
              The league moves from <span className="break-all font-mono text-ink">{host}</span> to <span className="break-all font-mono text-ink">{nextHost}</span> as soon as you confirm.
            </p>
            <p>
              Old links, QR codes, bookmarks and shared posts to <span className="break-all font-mono">{host}</span> stop working. League admins sign in at the new address.
            </p>
            <p>
              {customActive
                ? d.redirect_to_custom
                  ? `${d.custom_domain} keeps working, and the new subdomain redirects to it.`
                  : `${d.custom_domain} keeps working.`
                : d.custom_domain
                  ? `The custom domain ${d.custom_domain} isn't verified yet, so fans reach the league only at the new subdomain.`
                  : 'There is no custom domain, so fans reach the league only at the new subdomain.'}
            </p>
          </div>
        }
        onConfirm={async () => {
          try {
            const r = await api.tenants.changeSubdomain(tenant.id, next)
            apply(r.tenant)
            setResult(r.provisioning)
          } catch (err) {
            if (isApiError(err) && err.field === 'subdomain') setError(err.message)
            throw err
          }
        }}
      />
    </Card>
  )
}

// ─── Custom domain ──────────────────────────────────────────────────────────

function CustomDomainCard({ tenant }: { tenant: TenantDetail }) {
  const { apply } = useTenant()
  const d = tenant.domains
  const whiteLabel = tenant.addons.white_label
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<ProvisioningResult | null>(null)
  const [removeOpen, setRemoveOpen] = useState(false)

  const normalized = normalizeDomainInput(draft)
  const locked = !whiteLabel

  const submit = async () => {
    if (!normalized) return
    if (!DOMAIN_RE.test(normalized)) {
      setError('That isn’t a valid domain name, e.g. yourleague.com or play.yourleague.com.')
      return
    }
    setPending(true)
    setError(null)
    setResult(null)
    try {
      const r = await api.tenants.changeCustomDomain(tenant.id, normalized)
      apply(r.tenant)
      setResult(r.provisioning)
      setDraft('')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Card>
      <CardHeader title="Custom domain" description="Part of White-label. Fans reach the league at the league's own domain." />

      {locked ? (
        <InlineAlert tone="warn" className="mb-4" title="White-label is off">
          Custom domains come with the White-label add-on.{' '}
          <Link to="../add-ons" className="font-semibold underline underline-offset-2">
            Turn it on in Add-ons
          </Link>
          {d.custom_domain ? ' to change this domain. You can still remove it.' : ' first.'}
        </InlineAlert>
      ) : null}

      {d.custom_domain ? (
        <KeyValueList
          items={[
            {
              label: 'Domain',
              value: (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="break-all font-mono text-xs">{d.custom_domain}</span>
                  {STATE_BADGE[d.custom_domain_state]}
                </span>
              ),
            },
            { label: 'Verified', value: d.custom_domain_verified_at ? formatDate(d.custom_domain_verified_at) : <span className="text-muted">Not yet</span> },
          ]}
        />
      ) : (
        <p className="text-[13px] text-muted">No custom domain. The league is only at {d.subdomain_host}.</p>
      )}

      <form
        className="mt-4 grid gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <FormField
          label={d.custom_domain ? 'Replace with' : 'Domain'}
          error={error}
          hint={normalized && normalized !== draft.trim() ? <>Saved as <span className="font-mono text-ink">{normalized}</span></> : 'Without https:// or a path, e.g. yourleague.com or play.yourleague.com.'}
        >
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              mono
              autoCapitalize="off"
              autoComplete="off"
              spellCheck={false}
              placeholder="yourleague.com"
              value={draft}
              disabled={locked || pending}
              onChange={(e) => {
                setError(null)
                setDraft(e.target.value)
              }}
            />
          )}
        </FormField>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={locked || !normalized || normalized === d.custom_domain} loading={pending}>
            {d.custom_domain ? 'Replace domain' : 'Set custom domain'}
          </Button>
          {d.custom_domain ? (
            <Button variant="ghost" onClick={() => setRemoveOpen(true)}>
              Remove domain…
            </Button>
          ) : null}
        </div>
        {result ? <ProvisioningNote result={result} /> : null}
      </form>

      {d.custom_domain ? (
        <>
          <div className="mt-4 border-t border-line pt-4">
            <ToggleRow
              label="Redirect the subdomain to this domain"
              description={`Sends ${d.subdomain_host} visitors to ${d.custom_domain} (one canonical address for search engines). Only takes effect once the domain is verified.`}
              checked={d.redirect_to_custom}
              onToggle={async (next) => apply(await api.tenants.update(tenant.id, { redirect_to_custom: next }))}
            />
          </div>
          <DomainStatusPanel tenant={tenant} />
        </>
      ) : null}

      <ConfirmDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title={`Remove ${d.custom_domain ?? 'the custom domain'}?`}
        description={`Fans going to ${d.custom_domain} get an error until the customer points it elsewhere. ${d.subdomain_host} keeps working, and the redirect is turned off.`}
        confirmLabel="Remove domain"
        onConfirm={async () => {
          const r = await api.tenants.changeCustomDomain(tenant.id, null)
          apply(r.tenant)
          setResult(null)
        }}
      />
    </Card>
  )
}

// ─── Cloudflare status + DNS instructions ───────────────────────────────────

function DomainStatusPanel({ tenant }: { tenant: TenantDetail }) {
  const { reload } = useTenant()
  const d = tenant.domains
  const status = useQuery(['tenant', tenant.id, 'domain-status', d.custom_domain], (signal) => api.tenants.customDomainStatus(tenant.id, signal), { keepPrevious: true })
  const [registering, setRegistering] = useState(false)
  const [registerError, setRegisterError] = useState<string | null>(null)
  const s = status.data
  const cfStatus = (s?.cf?.status ?? '').toLowerCase()
  const polling = !!s?.cf && (cfStatus.includes('pending') || cfStatus.includes('provision'))
  const { refetch } = status

  useEffect(() => {
    if (!polling) return
    const t = setInterval(refetch, POLL_MS)
    return () => clearInterval(t)
  }, [polling, refetch])

  const register = async () => {
    setRegistering(true)
    setRegisterError(null)
    try {
      await api.tenants.registerCustomDomain(tenant.id)
      reload()
      refetch()
    } catch (err) {
      setRegisterError(errorMessage(err))
    } finally {
      setRegistering(false)
    }
  }

  const host = d.custom_domain!
  const target = d.cname_target ?? DEFAULT_CNAME_TARGET

  return (
    <div className="mt-4 grid gap-4 border-t border-line pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-sans text-2xs font-bold uppercase tracking-eyebrow text-muted">Cloudflare status</h3>
        <div className="flex items-center gap-2">
          {s?.cf ? <CfBadge status={s.cf.status} /> : null}
          <Button size="sm" variant="ghost" onClick={refetch} loading={status.isFetching} icon={<RefreshIcon size={14} />} aria-label="Re-check domain status">
            Re-check
          </Button>
        </div>
      </div>

      {status.status === 'loading' && !s ? (
        <SkeletonRows rows={2} label="Checking the domain" />
      ) : status.status === 'error' && !s ? (
        <InlineAlert tone="bad" title="Couldn’t check the domain">
          {errorMessage(status.error)}
        </InlineAlert>
      ) : s ? (
        <>
          {!s.configured ? (
            <InlineAlert tone="warn" title="Cloudflare Pages isn’t configured on this backend">
              Hostnames aren’t provisioned automatically. Set <code className="font-mono">CLOUDFLARE_API_TOKEN</code>, <code className="font-mono">CLOUDFLARE_ACCOUNT_ID</code> and{' '}
              <code className="font-mono">CLOUDFLARE_PAGES_PROJECT</code> on the backend.
            </InlineAlert>
          ) : null}
          {s.message ? (
            <InlineAlert
              tone="info"
              action={
                s.configured && !s.cf ? (
                  <Button size="sm" variant="primary" onClick={register} loading={registering}>
                    Provision with Cloudflare Pages
                  </Button>
                ) : undefined
              }
            >
              {s.message}
            </InlineAlert>
          ) : s.configured && !s.cf ? (
            <div>
              <Button size="sm" variant="primary" onClick={register} loading={registering}>
                Provision with Cloudflare Pages
              </Button>
            </div>
          ) : null}
          {registerError ? <InlineAlert tone="bad">{registerError}</InlineAlert> : null}
          {s.cf?.verification_errors?.length ? <IssueList title="Verification issues" items={s.cf.verification_errors} /> : null}
          {s.cf?.ssl?.validation_errors?.length ? <IssueList title="SSL issues" items={s.cf.ssl.validation_errors.map((e) => e.message)} /> : null}
          {s.cf?.ssl?.status ? <p className="text-xs text-muted">SSL: {s.cf.ssl.status.replace(/_/g, ' ')}</p> : null}
          {polling ? <p className="text-xs text-muted">Re-checking every 10 seconds while Cloudflare finishes.</p> : null}
        </>
      ) : null}

      <DnsInstructions host={host} target={target} status={s ?? null} verified={d.custom_domain_state === 'active'} />
    </div>
  )
}

function CfBadge({ status }: { status: string }) {
  const s = status.toLowerCase()
  const tone = s === 'active' ? 'good' : s.includes('pending') || s.includes('provision') ? 'warn' : 'bad'
  return <Badge tone={tone}>{status.replace(/_/g, ' ')}</Badge>
}

function IssueList({ title, items }: { title: string; items: string[] }) {
  return (
    <InlineAlert tone="bad" title={title}>
      <ul className="list-disc pl-4">
        {items.map((i, idx) => (
          <li key={idx}>{i}</li>
        ))}
      </ul>
    </InlineAlert>
  )
}

interface DnsRecord {
  type: string
  name: string
  value: string
  ttl?: string
  note?: string
}

function DnsInstructions({ host, target, status, verified }: { host: string; target: string; status: CustomDomainStatus | null; verified: boolean }) {
  const apex = isApex(host)
  const records: DnsRecord[] = apex
    ? [
        { type: 'ALIAS or ANAME', name: '@', value: target, ttl: 'Auto (or 300)', note: `The root of ${host}` },
        { type: 'CNAME', name: 'www', value: target, ttl: 'Auto (or 300)', note: 'So www. visitors land too' },
      ]
    : [{ type: 'CNAME', name: host, value: target, ttl: 'Auto (or 300)', note: `Some providers want just “${host.split('.')[0]}”` }]
  const cf = status?.cf
  if (cf?.ownership_verification?.name && cf.ownership_verification.value) {
    records.push({ type: 'TXT', name: cf.ownership_verification.name, value: cf.ownership_verification.value, note: 'Ownership check' })
  }
  for (const v of cf?.ssl?.validation_records ?? []) {
    if (v.txt_name && v.txt_value) records.push({ type: 'TXT', name: v.txt_name, value: v.txt_value, note: 'SSL validation' })
  }

  return (
    <section aria-labelledby="dns-heading" className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="dns-heading" className="font-sans text-2xs font-bold uppercase tracking-eyebrow text-muted">
          What to send the customer · {apex ? 'root domain' : 'subdomain'}
        </h3>
        {verified ? <Badge tone="good">Already working</Badge> : null}
      </div>
      <div className="grid gap-2 text-[13px] leading-relaxed text-ink/90">
        {apex ? (
          <>
            <p>
              <span className="break-all font-semibold text-ink">{host}</span> is a root (apex) domain. Most DNS providers can’t put a CNAME at the root, so it needs an <span className="font-mono">ALIAS</span> or{' '}
              <span className="font-mono">ANAME</span> record (Cloudflare, Route 53, DNSimple, Namecheap, Hover and Squarespace all have one).
            </p>
            <p>Ask them to delete any existing record at the root (often shown as @), add both records below, and keep the www one so www. visitors land too.</p>
            <p className="text-muted">
              <span className="font-semibold text-ink">DNS already on Cloudflare?</span> A plain CNAME at @ works: Cloudflare flattens it at the root. No ALIAS/ANAME at all (GoDaddy classic is the usual holdout)? Move DNS to
              Cloudflare, or ask us for the A-record IPs.
            </p>
          </>
        ) : (
          <p>
            <span className="break-all font-semibold text-ink">{host}</span> is a subdomain, so the customer adds one CNAME record at their DNS provider.
          </p>
        )}
        <p className="text-xs text-muted">SSL is issued automatically once the records propagate, usually within about 5 minutes.</p>
      </div>
      <ul className="grid gap-2" aria-label="DNS records">
        {records.map((r, i) => (
          <li key={`${r.type}-${r.name}-${i}`} className="grid gap-2 rounded-md border border-line bg-canvas/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-xs font-semibold text-ink">{r.type}</span>
              <CopyButton value={r.value} label="Copy value" ariaLabel={`Copy ${r.type} value ${r.value}`} />
            </div>
            <dl className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted">Name</dt>
              <dd className="min-w-0 break-all font-mono text-ink">{r.name}</dd>
              <dt className="text-muted">Value</dt>
              <dd className="min-w-0 break-all font-mono text-accent-ink">{r.value}</dd>
              {r.ttl ? (
                <>
                  <dt className="text-muted">TTL</dt>
                  <dd className="font-mono text-ink">{r.ttl}</dd>
                </>
              ) : null}
            </dl>
            {r.note ? <p className="text-2xs text-subtle">{r.note}</p> : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
